import { useState, useEffect, useMemo } from 'react'
import { FiCheck } from 'react-icons/fi'
import api from '../../../utils/api'
import Select from 'react-select'
import SelectStyles from '../../../shared/css/SelectStyle'
import Input from '../../../shared/components/form/input'
import Button from '../../../shared/components/Button/Button'
import FormLayout from '../../../shared/components/FormLayout/FormLayout'
import useFlashMessage from '../../../hooks/useFlashMessage'
import '../Cost/ApplyCostToColors.css'

// Espelha o ApplyCostToColors (marcar várias cores de uma vez), só trocando o
// eixo: aqui escolhe-se um Tecido fixo e marca quais cores (já escopadas pelo
// fornecedor do contexto) existem nele.
function AssociateColorsToCloth({ providerId, btCancelAction, handleOnSuccess }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [colors, setColors] = useState([])
    const [cloths, setCloths] = useState([])
    const [colorTypes, setColorTypes] = useState([])
    const [loading, setLoading] = useState(true)
    const [cloth, setCloth] = useState(null)
    const [checked, setChecked] = useState(new Set())
    const [initiallyChecked, setInitiallyChecked] = useState(new Set())
    const [saving, setSaving] = useState(false)
    const [search, setSearch] = useState('')
    const [colorTypeFilter, setColorTypeFilter] = useState(null)

    useEffect(() => {
        api.get('color/getall', {
            params: { provider: providerId },
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response.data) {
                setColors(response.data.colors)
                setCloths(response.data.cloths)
                setColorTypes(response.data.colorTypes)
                setLoading(false)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, providerId])

    const clothOptions = useMemo(() => cloths.map((c) => ({ value: c._id, label: c.name })), [cloths])
    const colorTypeOptions = useMemo(() => colorTypes.map((c) => ({ value: c._id, label: c.name })), [colorTypes])

    const filteredColors = useMemo(() => {
        const searchLowerCase = search.trim().toLowerCase()
        return colors.filter((item) => {
            const matchSearch = !searchLowerCase || item.name?.toLowerCase().includes(searchLowerCase)
            const matchColorType = !colorTypeFilter || item.colorType?._id === colorTypeFilter.value
            return matchSearch && matchColorType
        })
    }, [colors, search, colorTypeFilter])

    const groups = useMemo(() => {
        const list = []
        const indexByType = {}
        filteredColors.forEach((item) => {
            const key = item.colorType?._id || 'sem-tipo'
            if (indexByType[key] === undefined) {
                indexByType[key] = list.length
                list.push({ typeId: key, typeName: item.colorType?.name || 'Sem tipo', items: [] })
            }
            list[indexByType[key]].items.push(item)
        })
        return list
    }, [filteredColors])

    const toggleItem = (item) => {
        setChecked((prev) => {
            const next = new Set(prev)
            if (next.has(item._id)) next.delete(item._id)
            else next.add(item._id)
            return next
        })
    }

    const markAllInGroup = (group) => {
        setChecked((prev) => {
            const next = new Set(prev)
            group.items.forEach((item) => next.add(item._id))
            return next
        })
    }

    const selectedCount = checked.size

    function handleClothSelect(option) {
        setCloth(option)
        const already = new Set(
            colors.filter((item) => item.cloths?.some((c) => c._id === option.value)).map((item) => item._id)
        )
        setChecked(already)
        setInitiallyChecked(already)
    }

    async function submit() {
        if (!cloth) {
            setFlashMessage('Selecione um tecido', 'error')
            return
        }

        const toAdd = Array.from(checked).filter((id) => !initiallyChecked.has(id))
        const toRemove = Array.from(initiallyChecked).filter((id) => !checked.has(id))

        if (!toAdd.length && !toRemove.length) {
            setFlashMessage('Nenhuma alteração para salvar', 'error')
            return
        }

        setSaving(true)
        let msgType = 'success'
        let message = ''

        if (toAdd.length) {
            const data = await api.post('/color/bulk-apply-cloth', { cloth: cloth.value, colorIds: toAdd }, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data)
                .catch((err) => { msgType = 'error'; return err.response.data })
            message = data.message
        }

        if (msgType !== 'error' && toRemove.length) {
            const data = await api.post('/color/bulk-remove-cloth', { cloth: cloth.value, colorIds: toRemove }, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` },
            }).then((response) => response.data)
                .catch((err) => { msgType = 'error'; return err.response.data })
            message = data.message
        }

        setSaving(false)
        setFlashMessage(message, msgType)
        if (msgType !== 'error') {
            handleOnSuccess()
        }
    }

    return (
        <FormLayout
            title="Associar Cor ao Tecido"
            actions={
                cloth ? (
                    <>
                        <Button variant="save" onClick={submit} disabled={saving || loading}>Salvar</Button>
                        <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                        <span className="actc-count">{selectedCount} selecionada(s)</span>
                    </>
                ) : (
                    <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                )
            }
            headerExtra={
                <div className="actc-filters">
                    <div className="actc-filter-select">
                        <Select
                            options={clothOptions}
                            styles={SelectStyles}
                            onChange={handleClothSelect}
                            value={cloth}
                            placeholder="Selecione o tecido..."
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    {cloth && (
                        <>
                            <div className="actc-filter-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar por nome da cor..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <div className="actc-filter-select">
                                <Select
                                    isClearable
                                    options={colorTypeOptions}
                                    styles={SelectStyles}
                                    onChange={setColorTypeFilter}
                                    value={colorTypeFilter}
                                    placeholder="Filtrar por tipo de cor..."
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                        </>
                    )}
                </div>
            }
        >
            {!cloth ? (
                <p className="actc-hint">Escolha um tecido pra marcar quais cores existem nele.</p>
            ) : loading ? (
                <p className="pg-loading">Carregando cores...</p>
            ) : groups.length === 0 ? (
                <p className="actc-hint">Nenhuma cor encontrada para esse filtro.</p>
            ) : (
                <div className="actc-groups">
                    {groups.map((group) => (
                        <div key={group.typeId} className="actc-group">
                            <div className="actc-group-header">
                                <p className="actc-group-title">{group.typeName}</p>
                                <button type="button" className="actc-mark-all" onClick={() => markAllInGroup(group)}>Marcar todas</button>
                            </div>
                            <div className="actc-checkbox-list">
                                {group.items.map((item) => {
                                    const wasAlreadyLinked = initiallyChecked.has(item._id)
                                    const isChecked = checked.has(item._id)
                                    return (
                                        <div
                                            key={item._id}
                                            className="actc-checkbox-row"
                                            onClick={() => toggleItem(item)}
                                        >
                                            <div className="actc-row-main">
                                                <span className={`actc-checkbox ${isChecked ? 'actc-checkbox-checked' : ''}`}>
                                                    {isChecked && <FiCheck size={12} />}
                                                </span>
                                                <span className="actc-checkbox-label">{item.name}</span>
                                                {item.ref && <span className="actc-checkbox-provider">ref: {item.ref}</span>}
                                                {wasAlreadyLinked && isChecked && <span className="actc-already-tag">já associada a esse tecido</span>}
                                                {wasAlreadyLinked && !isChecked && <span className="actc-will-remove-tag">será desassociada</span>}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </FormLayout>
    )
}

export default AssociateColorsToCloth
